<template>
  <div class="max-w-7xl mx-auto space-y-5">
    <!-- Page Header -->
    <div v-if="canViewDeptAchievement" class="mb-6 flex items-center justify-between">
      <div>
        <h1 class="text-2xl text-gray-900">教研室管理</h1>
        <p class="text-sm text-gray-500 mt-1">查看和管理本教研室所有教师的成果</p>
      </div>
      <div class="text-sm text-gray-500">共 {{ filteredAchievements.length }} 项成果</div>
    </div>

    <!-- Permission Hint -->
    <el-card v-if="!canViewDeptAchievement" class="p-12 text-center mt-6">
      <h3 class="text-gray-900 mb-2">当前未开放时间段</h3>
      <p class="text-gray-600">请联系管理员或在开放期间再来查看教研室成果</p>
    </el-card>

    <!-- Filters -->
    <div v-if="canViewDeptAchievement" class="card p-6 mb-6">
      <div class="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-4">
        <!-- 搜索输入（与 React 对齐：无文字标签，左侧图标） -->
        <div class="flex-1">
          <el-input v-model="searchTerm" size="large" placeholder="搜索成果名称、教师或备注..." clearable>
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
        </div>

        <!-- 类型筛选 -->
        <div class="flex items-center space-x-2">
          <span class="text-sm text-gray-600 whitespace-nowrap">类型：</span>
          <el-select v-model="selectedType" size="large" class="w-32" popper-class="dm-select-popper">
            <el-option label="全部" value="全部" />
            <el-option label="教研项目" value="教研项目" />
            <el-option label="科研项目" value="科研项目" />
            <el-option label="学术会议" value="学术会议" />
            <el-option label="论文" value="论文" />
            <el-option label="专利" value="专利" />
            <el-option label="软著" value="软著" />
            <el-option label="专著" value="专著" />
            <el-option label="荣誉获奖" value="荣誉获奖" />
            <el-option label="其他" value="其他" />
          </el-select>
        </div>

        <!-- 归属筛选 -->
        <div class="flex items-center space-x-2">
          <span class="text-sm text-gray-600 whitespace-nowrap">归属：</span>
          <el-select v-model="selectedAchievementType" size="large" class="w-32" popper-class="dm-select-popper">
            <el-option label="全部" value="全部" />
            <el-option label="单人成果" value="单人成果" />
            <el-option label="多人成果" value="多人成果" />
          </el-select>
        </div>

        <!-- 教师筛选 -->
        <div class="flex items-center space-x-2">
          <span class="text-sm text-gray-600 whitespace-nowrap">教师：</span>
          <el-select v-model="selectedTeacher" size="large" class="w-32" filterable popper-class="dm-select-popper">
            <el-option label="全部" value="全部" />
            <el-option v-for="t in teacherOptions" :key="t" :label="t" :value="t" />
          </el-select>
        </div>
      </div>
    </div>

    <!-- Achievement List -->
    <div v-if="canViewDeptAchievement" class="space-y-4">
      <div
        v-for="item in currentAchievements"
        :key="item.id"
        class="card p-5 hover:shadow-md transition-shadow"
      >
        <div class="flex flex-col md:flex-row md:items-start gap-4">
          <div class="flex-1">
            <div class="flex items-center gap-3 mb-1">
              <span class="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800 border border-gray-200">{{ item.type }}</span>
              <span class="text-base text-gray-900 font-[550]">{{ item.title }}</span>
              <el-tag size="small" effect="plain" :class="item.achievementType === '单人成果' ? 'badge-outline-blue' : 'badge-outline-purple'">{{ item.achievementType }}</el-tag>
            </div>
            <div class="text-sm text-gray-600 flex items-center gap-4 flex-wrap">
              <span>教师：{{ item.teacherName }}</span>
              <span v-if="item.teacherNumber">工号：{{ item.teacherNumber }}</span>
              <span>提交：{{ formatDate(item.submitTime) }}</span>
              <span v-if="item.achievementType === '多人成果' && item.teamMembers && item.teamMembers.length" class="text-purple-600">团队成员：{{ item.teamMembers.length }}人</span>
            </div>
            <p v-if="item.remark" class="text-gray-600 text-sm mt-2 line-clamp-1">{{ item.remark }}</p>
          </div>
          <div class="flex items-center gap-2">
            <el-button v-if="canViewDeptAchievement" class="action-btn" plain @click="openView(item)">
              <el-icon class="mr-1"><View /></el-icon>查看
            </el-button>
            <el-button v-if="canEditDeptAchievement" class="action-btn action-edit" plain @click="openEdit(item)">
              <el-icon class="mr-1"><Edit /></el-icon>编辑
            </el-button>
            <el-button v-if="canDeleteDeptAchievement" class="action-btn action-delete" plain @click="openDelete(item)">
              <el-icon class="mr-1"><Delete /></el-icon>删除
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="canViewDeptAchievement && totalPages > 1" class="mt-6 flex justify-center">
      <nav role="navigation" aria-label="pagination" class="dm-pagination mx-auto flex w-full justify-center">
        <ul class="flex flex-row items-center gap-1">
          <!-- Previous -->
          <li>
            <button
              @click="goToPrevious"
              :disabled="currentPage === 1"
              class="inline-flex items-center justify-center gap-1 h-9 rounded-md text-sm font-medium transition-all px-2.5 sm:pl-2.5 disabled:pointer-events-none disabled:opacity-50 outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <ChevronLeft class="size-4" />
              <span class="hidden sm:block">Previous</span>
            </button>
          </li>

          <!-- 中间页码与省略号 -->
          <template v-for="pageNum in Array.from({length: totalPages}, (_, i) => i + 1)" :key="pageNum">
            <!-- 显示逻辑：一直显示第1页、最后一页、当前页及其前后各一页 -->
            <template v-if="pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)">
              <li>
                <button
                  @click="goToPage(pageNum)"
                  :aria-current="currentPage === pageNum ? 'page' : undefined"
                  :class="[
                    'inline-flex items-center justify-center w-9 h-9 rounded-md text-sm font-medium transition-all outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                    currentPage === pageNum
                      ? 'border bg-white text-gray-900'
                      : 'text-gray-900'
                  ]"
                >
                  {{ pageNum }}
                </button>
              </li>
            </template>
            <template v-else-if="pageNum === currentPage - 2 || pageNum === currentPage + 2">
              <li>
                <span class="flex w-9 h-9 items-center justify-center">
                  <MoreHorizontal class="size-4" />
                  <span class="sr-only">More pages</span>
                </span>
              </li>
            </template>
          </template>

          <!-- Next -->
          <li>
            <button
              @click="goToNext"
              :disabled="currentPage === totalPages"
              class="inline-flex items-center justify-center gap-1 h-9 rounded-md text-sm font-medium transition-all px-2.5 sm:pr-2.5 disabled:pointer-events-none disabled:opacity-50 outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <span class="hidden sm:block">Next</span>
              <ChevronRight class="size-4" />
            </button>
          </li>
        </ul>
      </nav>
    </div>

    <!-- View Dialog -->
    <el-dialog v-if="canViewDeptAchievement" v-model="viewDialogOpen" width="768px" class="max-h-[90vh] overflow-y-auto">
      <template #header>
        <div class="flex items-center gap-3">
          <div class="text-gray-900 text-xl" style="font-weight: 600">{{ selectedAchievement?.title || '查看成果' }}</div>
        </div>
      </template>
      <div v-if="selectedAchievement" class="space-y-6">
        <!-- 三行两列信息 -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div class="text-sm text-gray-600">成果类型</div>
            <div class="text-sm text-gray-900 mt-1">{{ selectedAchievement.type }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-600">归属类型</div>
            <div class="mt-1">
              <el-tag size="small" effect="plain" :class="selectedAchievement.achievementType === '单人成果' ? 'badge-outline-blue' : 'badge-outline-purple'">
                {{ selectedAchievement.achievementType }}
              </el-tag>
            </div>
          </div>
          <div>
            <div class="text-sm text-gray-600">教师姓名</div>
            <div class="text-sm text-gray-900 mt-1">{{ selectedAchievement.teacherName }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-600">教师工号</div>
            <div class="text-sm text-gray-900 mt-1">{{ selectedAchievement.teacherNumber }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-600">提交时间</div>
            <div class="text-sm text-gray-900 mt-1">{{ formatDate(selectedAchievement.submitTime) }}</div>
          </div>
          <div>
            <div class="text-sm text-gray-600">更新时间</div>
            <div class="text-sm text-gray-900 mt-1">{{ formatDate(selectedAchievement.updateTime) }}</div>
          </div>
        </div>

        <!-- 团队成员信息 -->
        <div v-if="selectedAchievement.teamMembers && selectedAchievement.teamMembers.length" class="space-y-2">
          <div class="text-sm text-gray-600">团队成员信息</div>
          <div class="mt-2 overflow-x-auto">
            <table class="w-full border-collapse">
              <thead>
                <tr class="bg-gray-50">
                  <th class="border border-gray-200 px-4 py-2 text-left text-sm text-gray-900 font-semibold">位次</th>
                  <th class="border border-gray-200 px-4 py-2 text-left text-sm text-gray-900 font-semibold">姓名</th>
                  <th class="border border-gray-200 px-4 py-2 text-left text-sm text-gray-900 font-semibold">身份</th>
                  <th class="border border-gray-200 px-4 py-2 text-left text-sm text-gray-900 font-semibold">所属单位</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(m, i) in selectedAchievement.teamMembers" :key="m.id">
                  <td class="border border-gray-200 px-4 py-2 text-sm text-gray-900">{{ i + 1 }}</td>
                  <td class="border border-gray-200 px-4 py-2 text-sm text-gray-900">{{ m.name }}</td>
                  <td class="border border-gray-200 px-4 py-2 text-sm text-gray-900">{{ identityLabel(m.identity) }}</td>
                  <td class="border border-gray-200 px-4 py-2 text-sm text-gray-900">{{ m.unit || '信息科学与工程学院' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 备注 -->
        <div v-if="selectedAchievement.remark">
          <div class="text-sm text-gray-600">备注</div>
          <p class="text-sm text-gray-900 mt-1 whitespace-pre-wrap">{{ selectedAchievement.remark }}</p>
        </div>

        <!-- 佐证材料 -->
        <div v-if="selectedAchievement.evidence && selectedAchievement.evidence.length">
          <div class="text-sm text-gray-600">佐证材料</div>
          <div class="mt-2 space-y-2">
            <div v-for="(file, idx) in selectedAchievement.evidence" :key="idx" class="flex items-center gap-2 p-2 bg-blue-50 rounded">
              <el-icon class="text-blue-500"><Document /></el-icon>
              <span class="text-blue-600 hover:underline cursor-pointer text-sm">{{ file }}</span>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- Delete Dialog -->
    <el-dialog v-if="canViewDeptAchievement" v-model="deleteDialogOpen" width="400px" class="custom-message-box" :show-close="false" align-center>
      <template #header="{ titleId, titleClass }">
        <div class="el-message-box__header">
          <div :id="titleId" :class="titleClass" class="el-message-box__title">确认删除</div>
        </div>
      </template>
      <div class="el-message-box__content">
        <div class="el-message-box__container">
          <div class="el-message-box__message">确定要删除这个成果吗？删除后无法恢复。</div>
        </div>
      </div>
      <template #footer>
        <div class="el-message-box__btns">
          <el-button class="custom-cancel-button" @click="deleteDialogOpen = false">取消</el-button>
          <el-button class="custom-confirm-button" type="danger" @click="confirmDelete">确定</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAppStore } from '@/stores'
import { ElMessage, ElMessageBox } from 'element-plus'
import { View, Edit, Delete, Document, Search } from '@element-plus/icons-vue'
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-vue-next'
import { getDepartmentTeachers, getDepartmentTeacherIds, getDepartmentAchievements } from '@/api/departmentManagement'
import { deleteAchievement as deleteAchievementAPI } from '@/api/achievement'
import { getPatent } from '@/api/patent'
import { getPaperById as getPaper } from '@/api/paper'
import { getSoftware } from '@/api/software'
import { getMonograph } from '@/api/monograph'
import { getHonor } from '@/api/honor'
import { getOther } from '@/api/other'
import { getAcademicConferenceById } from '@/api/academicConference'
import { getTeachingProject } from '@/api/teachingResearchProject'
import { getScientificResearchProject } from '@/api/scientificResearchProject'

const router = useRouter()
const store = useAppStore()
const { setActiveSection, setActiveTab } = store

// 数据状态
const loading = ref(false)
const teacherList = ref([])  // 教研室老师详细信息
const teacherIds = ref([])   // 教研室老师ID列表
const achievements = ref([]) // 成果列表
const total = ref(0)

// 筛选条件
const searchTerm = ref('')
const selectedType = ref('全部')
const selectedAchievementType = ref('全部')
const selectedTeacher = ref('全部')

// 分页
const itemsPerPage = 10
const currentPage = ref(1)

// 类型映射
const typeMapToCn = {
  'teachingResearchProject': '教研项目',
  'scientificResearchProject': '科研项目',
  'paper': '论文',
  'patent': '专利',
  'softwareCopyright': '软著',
  'monograph': '专著',
  'academicConference': '学术会议',
  'honoraryAward': '荣誉获奖',
  'otherAchievement': '其他'
}

// 教师选项：合并教研室接口与成果中的教师名（去重）
const teacherOptions = computed(() => {
  const namesFromList = teacherList.value.map(t => t.name).filter(Boolean)
  const namesFromAchievements = achievements.value.map(a => a.teacherName).filter(Boolean)
  const set = new Set([ ...namesFromList, ...namesFromAchievements ])
  return Array.from(set)
})

// 数据转换函数
const transformAchievementData = (item) => {
  const rawType = item.type || item.achievementType
  const rawTypeNormalized = rawType
  
  // 解析佐证材料
  let evidence = []
  if (item.evidenceMaterial) {
    try {
      evidence = typeof item.evidenceMaterial === 'string' 
        ? JSON.parse(item.evidenceMaterial) 
        : item.evidenceMaterial
      if (!Array.isArray(evidence)) evidence = []
    } catch {
      evidence = []
    }
  }
  
  return {
    id: item.id,
    detailId: item.detailId || item.detail_id || item.id,
    title: item.name || item.title,
    type: typeMapToCn[rawTypeNormalized] || item.type,
    rawType: rawType,
    rawTypeNormalized,
    achievementType: item.teamType === 1 || item.achievementType === 1 ? '单人成果' : '多人成果',
    teamMembers: (item.memberList || []).map(m => ({
      id: m.id || m.position,
      name: m.name,
      rank: m.position,
      identity: m.identity,
      unit: m.unit
    })),
    remark: item.description || item.remark || '',
    evidence: evidence,
    status: item.status === 1 ? '草稿' : '已提交',
    submitTime: item.createdTime || item.createTime,
    updateTime: item.updatedTime || item.updateTime,
    teacherId: item.createdBy || item.teacherId || item.userId || item.user_id,
    teacherName: item.teacherName || '',
    teacherNumber: ''
  }
}

// 页面加载时获取数据
onMounted(async () => {
  await loadTeachers()
  await fetchAchievements()
})

// 加载教研室老师
const loadTeachers = async () => {
  try {
    // 获取老师详细信息
    const res1 = await getDepartmentTeachers()
    teacherList.value = res1?.data || []
    
    // 获取老师ID列表
    const res2 = await getDepartmentTeacherIds()
    teacherIds.value = res2?.data || []
  } catch (error) {
    console.error('获取教研室老师失败:', error)
    ElMessage.error('获取教研室老师列表失败')
  }
}

// 查询成果列表：全量拉取后前端筛选与分页
const fetchAchievements = async () => {
  try {
    loading.value = true
    const PAGE_LIMIT = 100
    const MAX_PAGES = 1000
    const merged = []
    for (let pageIndex = 1; pageIndex <= MAX_PAGES; pageIndex++) {
      const res = await getDepartmentAchievements({ page: pageIndex, pageSize: PAGE_LIMIT })
      const page = res?.data ?? res
      const batch = Array.isArray(page?.records) ? page.records : []
      merged.push(...batch)
      const totalCount = Number(page?.total ?? merged.length)
      if (batch.length < PAGE_LIMIT || merged.length >= totalCount) break
    }
    // 转换数据并添加教师信息
    achievements.value = merged.map(item => {
      const tid = item.createdBy || item.teacherId || item.userId || item.user_id
      const teacher = teacherList.value.find(t => t.id === tid)
      const transformed = transformAchievementData(item)
      return {
        ...transformed,
        teacherName: teacher?.name || transformed.teacherName || '未知',
        teacherNumber: teacher?.employeeNumber || ''
      }
    })
    total.value = achievements.value.length
  } catch (error) {
    console.error('获取成果列表失败:', error)
    ElMessage.error('获取成果列表失败')
  } finally {
    loading.value = false
  }
}

// 筛选条件变化时仅重置页码，数据走前端筛选
watch([selectedType, selectedAchievementType, selectedTeacher], () => {
  currentPage.value = 1
})

// 前端筛选与搜索
const filteredAchievements = computed(() => {
  let list = achievements.value.slice()
  // 过滤草稿
  list = list.filter(a => {
    const v = typeof a.status === 'string' ? a.status.trim().toLowerCase() : a.status
    return v !== '草稿' && v !== 'draft' && v !== 1 && v !== '1'
  })
  // 类型筛选
  if (selectedType.value !== '全部') {
    list = list.filter(a => a.type === selectedType.value)
  }
  // 归属筛选
  if (selectedAchievementType.value !== '全部') {
    list = list.filter(a => a.achievementType === selectedAchievementType.value)
  }
  // 教师筛选
  if (selectedTeacher.value !== '全部') {
    list = list.filter(a => a.teacherName === selectedTeacher.value)
  }
  // 搜索
  const s = searchTerm.value.trim().toLowerCase()
  if (s) {
    list = list.filter(a => {
      return String(a.title || '').toLowerCase().includes(s) ||
             String(a.teacherName || '').toLowerCase().includes(s) ||
             String(a.remark || '').toLowerCase().includes(s)
    })
  }
  return list
})

// 分页数据
const totalPages = computed(() => Math.ceil(filteredAchievements.value.length / itemsPerPage))
const currentAchievements = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage
  return filteredAchievements.value.slice(start, start + itemsPerPage)
})

// 分页方法
function goToPrevious() {
  currentPage.value = Math.max(1, currentPage.value - 1)
}
function goToNext() {
  currentPage.value = Math.min(totalPages.value, currentPage.value + 1)
}
function goToPage(pageNum) {
  currentPage.value = pageNum
}

// 对话框状态
const selectedAchievement = ref(null)
const viewDialogOpen = ref(false)
const deleteDialogOpen = ref(false)

// 查看成果（复用MyAchievements逻辑）
const openView = async (achievement) => {
  try {
    loading.value = true
    selectedAchievement.value = { ...achievement }
    viewDialogOpen.value = true
    
    // 调用专用详情接口获取完整数据
    const detailId = achievement.detailId
    if (detailId) {
      const fullData = await getSpecificDetail(achievement.type, detailId)
      if (fullData) {
        const transformedFull = transformAchievementData(fullData)
        selectedAchievement.value = {
          ...achievement,
          teamMembers: transformedFull.teamMembers?.length > 0 
            ? transformedFull.teamMembers 
            : achievement.teamMembers,
          evidence: transformedFull.evidence?.length > 0 
            ? transformedFull.evidence 
            : achievement.evidence,
          remark: transformedFull.remark || achievement.remark
        }
      }
    }
  } catch (error) {
    console.error('查看成果失败:', error)
  } finally {
    loading.value = false
  }
}

// 根据成果类型调用对应的专用详情接口
async function getSpecificDetail(type, detailId) {
  try {
    const apiMap = {
      '专利': getPatent,
      '论文': getPaper,
      '软著': getSoftware,
      '专著': getMonograph,
      '荣誉获奖': getHonor,
      '其他': getOther,
      '学术会议': getAcademicConferenceById,
      '教研项目': getTeachingProject,
      '科研项目': getScientificResearchProject
    }
    
    const apiFunc = apiMap[type]
    if (!apiFunc) return null
    
    const res = await apiFunc(detailId)
    return res?.data ?? res
  } catch (error) {
    console.error('获取详情失败:', error)
    return null
  }
}

// 编辑成果
function openEdit(item) {
  const typeMap = {
    '教研项目': 'teaching-project',
    '科研项目': 'research-project',
    '学术会议': 'conference',
    '论文': 'paper',
    '专利': 'patent',
    '软著': 'software',
    '专著': 'monograph',
    '荣誉获奖': 'award',
    '其他': 'other'
  }
  
  const type = item.type
  const tab = typeMap[type]
  
  if (tab) {
    // 使用 detailId 进行跳转
    const detailId = item.detailId || item.id
    
    // 切换到填报中心并选中对应标签
    setActiveSection('report')
    setActiveTab(tab)
    
    // 跳转到首页（填报中心），带上编辑参数
    router.push({
      path: '/',
      query: { id: detailId, mode: 'edit' }
    })
  } else {
    ElMessage.warning(`未知的成果类型: ${type}`)
  }
}

// 删除成果（复用MyAchievements逻辑）
const openDelete = async (achievement) => {
  try {
    await ElMessageBox.confirm(
      '确定要删除这个成果吗？删除后无法恢复。',
      '确认删除',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
        customClass: 'custom-message-box',
        confirmButtonClass: 'custom-confirm-button',
        cancelButtonClass: 'custom-cancel-button'
      }
    )
    
    loading.value = true
    
    // 关键修正：使用 detailId（详情表ID）而不是 id（汇总表ID）
    const detailId = achievement.detailId || achievement.id
    const deleteParams = {
      type: achievement.rawTypeNormalized || achievement.rawType,
      id: detailId  // 后端需要的是详情表ID
    }
    
    
    await deleteAchievementAPI(deleteParams)
    
    ElMessage.success('删除成功')
    await fetchAchievements()  // 刷新列表
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除成果失败:', error)
      ElMessage.error('删除失败，请稍后重试')
    }
  } finally {
    loading.value = false
  }
}

// 工具函数
function formatDate(d) {
  if (!d) return '-'
  const dt = typeof d === 'string' ? new Date(d) : d
  const y = dt.getFullYear()
  const m = String(dt.getMonth() + 1).padStart(2, '0')
  const day = String(dt.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function identityLabel(id) {
  const map = { teacher: '教师', student: '学生', external: '校外人员' }
  return map[id] || id
}

const canViewDeptAchievement = computed(() => store.hasFunctionByName('查看教研室成果'))
const canEditDeptAchievement = computed(() => store.hasFunctionByName('编辑教研室成果'))
const canDeleteDeptAchievement = computed(() => store.hasFunctionByName('删除教研室成果'))

</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card {
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background-color: white;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
}
.badge-outline-blue {
  color: #2563eb;
  border: 1px solid #bfdbfe;
  background-color: #ffffff;
}

.badge-outline-purple {
  color: #7c3aed;
  border: 1px solid #d8b4fe;
  background-color: #ffffff;
}

.el-button.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  background-color: #ffffff;
  color: #374151;
  border: 1px solid #d1d5db;
}

.el-button.action-btn:hover {
  background-color: #f9fafb;
}

.el-button.action-edit {
  color: #2563eb;
  border-color: #bfdbfe;
}

.el-button.action-edit:hover {
  border-color: #93c5fd;
}

.el-button.action-delete {
  color: #dc2626;
  border-color: #fecaca;
}

.el-button.action-delete:hover {
  border-color: #fca5a5;
}

.el-button.action-btn .el-icon {
  font-size: 16px;
}

:deep(.el-card) {
  border-radius: 8px;
}

:deep(.el-input__wrapper),
:deep(.el-select .el-input__wrapper) {
  border-radius: 8px;
}

/* 下拉框选项样式 - 灰色字体、灰色悬停背景 */
:deep(.dm-select-popper) {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  background-color: #ffffff;
}
:deep(.dm-select-popper .el-select-dropdown__item) {
  border-radius: 6px;
  color: #6b7280 !important;
}
:deep(.dm-select-popper .el-select-dropdown__item:hover) {
  background-color: #e5e7eb !important;
  color: #111827 !important;
}
:deep(.dm-select-popper .el-select-dropdown__item.is-selected) {
  color: #6b7280 !important;
  background-color: #f3f4f6 !important;
  font-weight: 500;
}
:deep(.dm-select-popper .el-select-dropdown__item.is-hovering) {
  background-color: #e5e7eb !important;
  color: #111827 !important;
}

/* 分页按钮悬停统一为深灰底色，保持与"我的成果"一致感 */
.dm-pagination button:hover {
  background-color: #e5e7eb;
}

/* 编辑对话框输入圆角更大以匹配截图视觉 */
:deep(.edit-dialog .el-input__wrapper) {
  border-radius: 12px;
}

/* 输入框和下拉框样式 - 与教师状态维护保持一致 */
:deep(.el-input__wrapper) {
  background-color: #f3f3f5;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: none;
  transition: all 0.2s;
}
:deep(.el-input__wrapper:hover) {
  border-color: rgba(0, 0, 0, 0.2);
  box-shadow: none;
}
:deep(.el-input__wrapper.is-focus) {
  border-color: rgba(0, 0, 0, 0.3);
  box-shadow: none;
}

/* 删除确认对话框样式 - 与"我的成果"保持一致 */
:deep(.custom-message-box) {
  border-radius: 12px;
  padding: 24px;
}

:deep(.custom-message-box .el-message-box__header) {
  padding-bottom: 16px;
}

:deep(.custom-message-box .el-message-box__title) {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

:deep(.custom-message-box .el-message-box__content) {
  padding: 16px 0;
  color: #6b7280;
}

:deep(.custom-confirm-button) {
  background: #ef4444 !important;
  border-color: #ef4444 !important;
  color: white !important;
  padding: 8px 20px;
  border-radius: 6px;
  font-weight: 500;
}

:deep(.custom-confirm-button:hover) {
  background: #dc2626 !important;
  border-color: #dc2626 !important;
}

:deep(.custom-cancel-button) {
  padding: 8px 20px;
  border-radius: 6px;
  font-weight: 500;
}
</style>
